
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export interface Message {
  id: string;
  sender_id: string;
  recipient_id: string;
  content: string;
  created_at: string;
  read: boolean;
  conversation_id: string;
}

export interface Conversation {
  id: string;
  retailer_id: string;
  supplier_id: string;
  created_at: string;
  updated_at: string;
  last_message: string | null;
  last_message_at: string;
  supplier_profile?: {
    name: string;
    business_name: string | null;
    profile_image: string | null;
  };
  retailer_profile?: {
    name: string;
    business_name: string | null;
    profile_image: string | null;
  };
}

// Function to get or create a conversation between two users
export async function getOrCreateConversation(
  retailerId: string,
  supplierId: string
): Promise<string | null> {
  try {
    // First check if a conversation already exists
    const { data: existingConversations, error: fetchError } = await supabase
      .from("conversations")
      .select("id")
      .match({ retailer_id: retailerId, supplier_id: supplierId });

    if (fetchError) throw fetchError;

    // If conversation exists, return the id
    if (existingConversations && existingConversations.length > 0) {
      return existingConversations[0].id;
    }

    // If not, create a new conversation
    const { data: newConversation, error: insertError } = await supabase
      .from("conversations")
      .insert({
        retailer_id: retailerId,
        supplier_id: supplierId,
        last_message: null,
        last_message_at: new Date().toISOString(),
      })
      .select("id")
      .single();

    if (insertError) throw insertError;
    return newConversation.id;
  } catch (error) {
    console.error("Error getting or creating conversation:", error);
    toast({
      title: "Error",
      description: "Could not create conversation. Please try again.",
      variant: "destructive",
    });
    return null;
  }
}

// Function to send a message
export async function sendMessage(
  senderId: string,
  recipientId: string,
  content: string,
  conversationId: string
): Promise<boolean> {
  try {
    // Insert message
    const { data: messageData, error: messageError } = await supabase
      .from("messages")
      .insert({
        sender_id: senderId,
        recipient_id: recipientId,
        content,
        conversation_id: conversationId,
      })
      .select("*")
      .single();

    if (messageError) throw messageError;

    // Explicitly update conversation with last message and timestamp
    const currentTime = new Date().toISOString();
    const { error: conversationError } = await supabase
      .from("conversations")
      .update({
        last_message: content,
        last_message_at: currentTime,
      })
      .eq("id", conversationId);

    if (conversationError) {
      console.error("Error updating conversation:", conversationError);
      throw conversationError;
    }
    
    console.log("Conversation updated successfully with:", { 
      last_message: content, 
      last_message_at: currentTime 
    });
    
    return true;
  } catch (error) {
    console.error("Error sending message:", error);
    toast({
      title: "Error",
      description: "Could not send message. Please try again.",
      variant: "destructive",
    });
    return false;
  }
}

// Function to fetch user's conversations
export async function fetchConversations(userId: string): Promise<Conversation[]> {
  try {
    // First we need to fetch all conversations for the user
    const { data: conversations, error } = await supabase
      .from("conversations")
      .select(`*`)
      .or(`retailer_id.eq.${userId},supplier_id.eq.${userId}`)
      .order("last_message_at", { ascending: false });

    if (error) throw error;
    
    if (!conversations || conversations.length === 0) {
      return [];
    }
    
    // Now for each conversation, fetch the profile data for both retailer and supplier
    const conversationsWithProfiles = await Promise.all(
      conversations.map(async (conversation) => {
        // Fetch supplier profile
        const { data: supplierData, error: supplierError } = await supabase
          .from("profiles")
          .select("name, business_name, profile_image")
          .eq("id", conversation.supplier_id)
          .single();
          
        if (supplierError && supplierError.code !== 'PGRST116') {
          console.error("Error fetching supplier profile:", supplierError);
        }
        
        // Fetch retailer profile
        const { data: retailerData, error: retailerError } = await supabase
          .from("profiles")
          .select("name, business_name, profile_image")
          .eq("id", conversation.retailer_id)
          .single();
          
        if (retailerError && retailerError.code !== 'PGRST116') {
          console.error("Error fetching retailer profile:", retailerError);
        }
        
        return {
          ...conversation,
          supplier_profile: supplierData || undefined,
          retailer_profile: retailerData || undefined
        };
      })
    );
    
    return conversationsWithProfiles;
  } catch (error) {
    console.error("Error fetching conversations:", error);
    toast({
      title: "Error",
      description: "Could not load conversations. Please try again.",
      variant: "destructive",
    });
    return [];
  }
}

// Function to fetch messages for a conversation
export async function fetchMessages(conversationId: string): Promise<Message[]> {
  try {
    const { data, error } = await supabase
      .from("messages")
      .select("*")
      .eq("conversation_id", conversationId)
      .order("created_at", { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error fetching messages:", error);
    toast({
      title: "Error",
      description: "Could not load messages. Please try again.",
      variant: "destructive",
    });
    return [];
  }
}

// Function to mark messages as read
export async function markMessagesAsRead(
  conversationId: string,
  userId: string
): Promise<void> {
  try {
    const { error } = await supabase
      .from("messages")
      .update({ read: true })
      .eq("conversation_id", conversationId)
      .eq("recipient_id", userId)
      .eq("read", false);

    if (error) throw error;
  } catch (error) {
    console.error("Error marking messages as read:", error);
  }
}

// Get recipient ID based on conversation and current user
export function getRecipientId(conversation: Conversation, currentUserId: string): string {
  return conversation.retailer_id === currentUserId
    ? conversation.supplier_id
    : conversation.retailer_id;
}

// Get display name for a conversation participant
export function getDisplayName(conversation: Conversation, currentUserId: string): string {
  // If the current user is the retailer, show the supplier name
  if (conversation.retailer_id === currentUserId) {
    return conversation.supplier_profile?.business_name || 
           conversation.supplier_profile?.name || 
           "Supplier";
  }
  
  // Otherwise, show the retailer name
  return conversation.retailer_profile?.business_name || 
         conversation.retailer_profile?.name || 
         "Retailer";
}
